import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { stripePromise } from '../stripe';
import { CLUB_PAYS_PRICES, INVITE_PRICES } from '../utils/pricing';

const API_BASE = process.env.REACT_APP_API_URL || '';
const LOGO = '/dailyreps3.png';

const FEATURES = [
  'Full video drill library',
  'AI-written descriptions',
  'Daily reinforcement questions',
  'Streaks, points, and shields',
  'Badges and team leaderboard',
  'Challenge days and bonus points',
];

export default function ClubOrder() {
  const location = useLocation();
  const navigate = useNavigate();
  const isAnnual = location.pathname.includes('order-yearly');

  const searchParams = new URLSearchParams(location.search);
  const [billingModel, setBillingModel] = useState('club_pays');
  const [error, setError] = useState('');
  const [showCheckout, setShowCheckout] = useState(false);

  // Affiliate tracking
  useEffect(() => {
    const s = document.createElement('script');
    s.src = 'https://affiliate-platform-production-f4fa.up.railway.app/track.js?pid=cmtdislyw024r2tx3gmysyb0g&v=1';
    s.async = true;
    document.head.appendChild(s);
    return () => { document.head.removeChild(s); };
  }, []);

  // Capture trialxyz param for testing shorter trials
  useEffect(() => {
    const t = searchParams.get('trialxyz');
    if (t) localStorage.setItem('trialxyz', t);
  }, []); // eslint-disable-line

  const toggleInterval = () => {
    setShowCheckout(false);
    const t = searchParams.get('trialxyz');
    const extra = t ? `?trialxyz=${t}` : '';
    const newPath = isAnnual ? '/club/order-monthly' : '/club/order-yearly';
    navigate(`${newPath}${extra}`);
  };

  const fetchClientSecret = useCallback(async () => {
    const body = { plan: 'club_pays', interval: isAnnual ? 'annual' : 'monthly' };
    const aff = searchParams.get('aff');
    if (aff) body.affiliate_ref = aff;
    const t = localStorage.getItem('trialxyz');
    if (t) body.trial_days = parseInt(t, 10);
    const res = await fetch(`${API_BASE}/api/order/checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to start checkout');
    return data.client_secret;
  }, [isAnnual]);

  const handleCheckout = async () => {
    const t = searchParams.get('trialxyz') || localStorage.getItem('trialxyz');
    const affParam = searchParams.get('aff');
    const trialStr = t ? `&trialxyz=${t}` : '';
    const affStr = affParam ? `&aff=${affParam}` : '';
    if (billingModel === 'parent_pays') {
      const qs = [t ? `trialxyz=${t}` : '', affParam ? `aff=${affParam}` : ''].filter(Boolean).join('&');
      navigate(`/club/setup${qs ? '?' + qs : ''}`);
      return;
    }
    // Club pays: create account first, pay later from dashboard
    navigate(`/club/setup?billing_model=club_pays${trialStr}${affStr}`);
  };

  const handleBillingModelChange = (model) => {
    setBillingModel(model);
    setShowCheckout(false);
  };

  return (
    <div className="order-page">
      <header className="order-header"><div className="order-wrap">
        <div className="order-logo-row">
          <img src={LOGO} alt="Train Daily" className="order-logo" />
          <span className="order-brand">Train Daily</span>
        </div>
        <a href="https://www.traindaily.com/coaches" className="order-nav-link">For Coaches</a>
      </div></header>

      <main className="order-main"><div className="order-wrap">
        {!showCheckout && (
          <>
            <p className="order-eyebrow">Start your free trial</p>
            <h1 className="order-title">
              Club <span className="accent">Plan</span>
            </h1>
            <p className="order-lead">
              Every feature included. For clubs with 25+ players across multiple teams.
            </p>

            {/* Billing model selector */}
            <div className="order-billing-model">
              <button
                className={`order-billing-option ${billingModel === 'club_pays' ? 'active' : ''}`}
                onClick={() => handleBillingModelChange('club_pays')}
                type="button"
              >
                <strong>Club pays</strong>
                <span>Per-player pricing, billed to the club</span>
              </button>
              <button
                className={`order-billing-option ${billingModel === 'parent_pays' ? 'active' : ''}`}
                onClick={() => handleBillingModelChange('parent_pays')}
                type="button"
              >
                <strong>Parents pay</strong>
                <span>Free for you — each family pays their own</span>
              </button>
            </div>

            {billingModel === 'club_pays' && (
              <div className="order-toggle-wrap">
                <span className={`order-toggle-label ${!isAnnual ? 'on' : ''}`}>Monthly</span>
                <div className={`order-switch ${isAnnual ? 'annual' : ''}`} onClick={toggleInterval} role="button" tabIndex={0}></div>
                <span className={`order-toggle-label ${isAnnual ? 'on' : ''}`}>Annual <span className="order-toggle-save">Save 20%</span></span>
              </div>
            )}

            <div className="order-cards">
              <div className="order-card">
                {billingModel === 'club_pays' ? (
                  <>
                    <h3>Club</h3>
                    <div className="order-card-subtitle">Per player on your roster</div>
                    <div className="order-price">{isAnnual ? CLUB_PAYS_PRICES.annualPerMonth : CLUB_PAYS_PRICES.monthly}</div>
                    <div className="order-price-per">per player / month</div>
                    <div className="order-price-billed">{isAnnual ? `${CLUB_PAYS_PRICES.annual} per player, billed annually` : 'Billed monthly'}</div>
                  </>
                ) : (
                  <>
                    <h3>Free for your club</h3>
                    <div className="order-card-subtitle">Parents pay per player</div>
                    <div className="order-price">$0</div>
                    <div className="order-price-per">for you</div>
                    <div className="order-price-billed">Parents pay {INVITE_PRICES.club_parent_pays.monthly}/player/mo or {INVITE_PRICES.club_parent_pays.annual}/player/yr</div>
                  </>
                )}
                <ul className="order-features">
                  {FEATURES.map((f, i) => (
                    <li key={i}><i className="fas fa-check"></i> {f}</li>
                  ))}
                </ul>
                {error && <div className="order-error">{error}</div>}
                <button className="btn btn-primary order-btn" onClick={handleCheckout}>
                  {billingModel === 'parent_pays' ? 'Get Started Free' : 'Start your 30-day free trial'}
                </button>
              </div>
            </div>

            {billingModel === 'club_pays' && (
              <p className="order-fine">
                30-day free trial, card required. Choose monthly or annual at signup. Cancel before day 30 and pay nothing.
                Roster changes are trued up monthly, so you only ever pay for the players you have.
              </p>
            )}
            {billingModel === 'parent_pays' && (
              <p className="order-fine">
                Create your club account for free. Then generate invite links for your players' families.
                Each family pays their own subscription — you manage the training.
              </p>
            )}
          </>
        )}

        {showCheckout && (
          <div className="order-checkout-embed">
            <button
              className="order-checkout-back"
              onClick={() => setShowCheckout(false)}
              type="button"
            >
              <i className="fas fa-arrow-left"></i> Back to pricing
            </button>
            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ fetchClientSecret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        )}
      </div></main>

      <footer className="order-footer"><div className="order-wrap">
        <p>&copy; Train Daily. Built by Outsmart Press, Inc.</p>
      </div></footer>
    </div>
  );
}
